import React, { ComponentPropsWithoutRef, forwardRef, useContext } from 'react';
import { BoxStyleSpaceScale } from '../../legacy/Box';
import { Flex } from '../../legacy/Flex';
import { Pane } from '../../legacy/Pane';
import { SpaceScale } from '../../styled-system/types/scale';
import { identifyComponent } from '../../util';
import { ListContext, ListContextProps } from '../List/context';
import css from './index.module.scss';

export interface ListItemBaseProps extends ListContextProps, ComponentPropsWithoutRef<'li'> {
  /**
   * The content of the component. If a `ListItemSecondaryAction` is used it must
   * be the last child.
   */
  children: React.ReactNode;
  className?: string;
  role?: string;
  testId?: string;
}

export interface ListItemProps extends Omit<ListItemBaseProps, 'onClick'> {
  /** ListItem is not an interactable element. Use `MenuItem` instead. */
  onClick?: never;
}

export const ListItem = forwardRef<HTMLLIElement, ListItemProps>(function ListItem(
  {
    testId = 'list-item',
    children,
    compact: compactProp,
    disableGutters: disableGuttersProp,
    role,
    ...rest
  },
  ref,
) {
  const listContext = useContext(ListContext);
  const compact = compactProp ?? listContext.compact;
  const disableGutters = disableGuttersProp ?? listContext.disableGutters;
  const verticalPadding: SpaceScale = compact ? 4 : 8;
  const horizontalPadding: SpaceScale = disableGutters ? 0 : 12;
  const padding: BoxStyleSpaceScale = [verticalPadding, horizontalPadding];

  return (
    <li
      data-component="ListItem"
      data-testid={testId}
      ref={ref}
      {...rest}
      {...identifyComponent('list-item', rest)}
      className={css.listItem}
      role={role}
    >
      <Pane padding={padding}>
        <Flex justifyContent="flex-start" alignItems="center" gap={12}>
          {children}
        </Flex>
      </Pane>
    </li>
  );
});
